import React from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, IconButton, Snackbar, Typography } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { withStyles } from '@material-ui/core/styles';

import useAddProgramHook from './useAddProgramHook';
import styles from './add-program.style';
import { FormActions, ImageUpload, ProgramDetails, ProgramMetadata } from './components';
import DescriptionField from './components/description-field/description-field';
import AddCourse from './components/add-course/add-course';
import ConfirmationDialog from './components/confirmation-dialog';

const AddProgram = ({ classes, handleAddNew, handleBackToList, handleEditProgram, isEditingMode, programData }) => {
  const {
    formData,
    alertState,
    isFormValid,
    categories,
    handleChange,
    handleImageUpload,
    handleImageDelete,
    handleAlertClose,
    handleSubmit,
    getImageUrl,
    handleContentDeliveryMethodChange,
    handleContentProviderChange,
    handleCourseChange,
  } = useAddProgramHook(handleBackToList, handleAddNew, handleEditProgram, isEditingMode, programData);

  return (
    <Box className={classes.root} data-testid="add-program-root">
      <Box display="flex" alignItems="center" className={classes.backButton}>
        <IconButton
          data-testid="arrow-back-button"
          className={classes.arrowBackButton}
          aria-label="Back"
          onClick={() => handleBackToList()}
        >
          <ArrowBackIcon className={classes.arrowBackIcon} />
        </IconButton>
        <Typography variant="h4" className={classes.addProgramtext}>{isEditingMode ? 'Edit Program' : 'Add New Program'}</Typography>
      </Box>

      <Box className={classes.fieldGroup}>
          <ProgramDetails formData={formData} handleChange={handleChange} isEditingMode={isEditingMode} />

          <ImageUpload
            formData={formData}
            handleImageUpload={handleImageUpload}
            handleImageDelete={handleImageDelete}
            getImageUrl={getImageUrl}
          />

          <DescriptionField 
            formData={formData}
            handleChange={handleChange}
            title="Short Program Description"
            characterLimit={300}
            fieldNameEn="shortDescriptionEn"
            fieldNameEs="shortDescriptionEs"
          />
          <DescriptionField 
            formData={formData}
            handleChange={handleChange}
            title="Long Program Description"
            characterLimit={1000}
            fieldNameEn="longDescriptionEn"
            fieldNameEs="longDescriptionEs"
            multiline={true}
            minRows={8}
          />
        <ProgramMetadata formData={formData}
                         handleChange={handleChange}
                         isEditingMode={isEditingMode}
                         categories={categories} />

        <AddCourse
          formData={formData} 
          handleCourseChange={handleCourseChange}
        />

        <Box py={4}>
          <Divider />
        </Box>

          <FormActions 
            isFormValid={isFormValid} 
            handleBackToList={handleBackToList}
            handleSubmit={handleSubmit}
          />
        </Box>
      <Snackbar
        open={alertState.open}
        autoHideDuration={6000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert elevation={6} variant="filled" onClose={handleAlertClose} severity={alertState.severity}>
          {alertState.message}
        </MuiAlert>
      </Snackbar>

      <ConfirmationDialog
        open={formData.showDeleteDialog}
        onClose={() => handleImageDelete(false)}
        onConfirm={() => handleImageDelete(true)}
        title="Remove current thumbnail image?"
        message="Are you sure you want to remove the current image? This action cannot be undone. You will need to upload another image as the thumbnail."
        confirmText="Remove Image"
        cancelText="Cancel"
        confirmColor="secondary"
        classes={classes}
        testIds={{
          closeButton: 'dialog-close-button',
          confirmButton: 'remove-image-button',
          cancelButton: 'cancel-button'
        }}
      />

      <ConfirmationDialog
        open={formData.showDeliveryMethodChangeDialog}
        onClose={() => handleContentDeliveryMethodChange(false)}
        onConfirm={() => handleContentDeliveryMethodChange(true)}
        title="Change Content Delivery Method?"
        message="You're about to change the content delivery method. All the selected courses will be removed."
        confirmText="Continue"
        cancelText="Cancel"
        confirmColor="secondary"
        classes={classes}
        testIds={{
          closeButton: 'dialog-close-button',
          confirmButton: 'change-content-delivery-method-button',
          cancelButton: 'cancel-button'
        }}
      />

      <ConfirmationDialog
        open={formData.showContentProviderChangeDialog}
        onClose={() => handleContentProviderChange(false)}
        onConfirm={() => handleContentProviderChange(true)}
        title="Change Content Provider?"
        message="You're about to change the content provider. All the selected courses will be removed."
        confirmText="Continue"
        cancelText="Cancel"
        confirmColor="secondary"
        classes={classes}
        testIds={{
          closeButton: 'dialog-close-button',
          confirmButton: 'change-content-provider-button',
          cancelButton: 'cancel-button'
        }}
      />
    </Box>
  );
};

AddProgram.propTypes = {
  classes: PropTypes.object.isRequired,
  handleAddNew: PropTypes.func.isRequired,
  handleBackToList: PropTypes.func.isRequired,
  handleEditProgram: PropTypes.func.isRequired,
  isEditingMode: PropTypes.bool.isRequired,
  programData: PropTypes.object
};

AddProgram.defaultProps = {
  programData: null
};

export default withStyles(styles)(AddProgram);
